import React from 'react';
import { returnNationality } from '../actions/returnNationality';
import './style.scss';

/**
 * Kit grafico comune delle pagine (stessa grafica della sezione statistiche).
 * La pagina va avvolta in <Page>, che lascia lo spazio corretto per sidebar e header.
 */

export function Page({ className = '', children }) {
    return <div className={`uiPage ${className}`}>{children}</div>;
}

export function PageHeader({ title, subtitle, actions }) {
    return (
        <div className="uiPageHeader">
            <div>
                <h2>{title}</h2>
                {subtitle && <p>{subtitle}</p>}
            </div>
            {actions && <div className="uiActions">{actions}</div>}
        </div>
    );
}

export function Card({ title, subtitle, actions, className = '', children }) {
    return (
        <section className={`uiCard ${className}`}>
            {
                (title || actions) &&
                <header className="uiCardHeader">
                    <div>
                        {title && <h3>{title}</h3>}
                        {subtitle && <p>{subtitle}</p>}
                    </div>
                    {actions && <div className="uiActions">{actions}</div>}
                </header>
            }
            {children}
        </section>
    );
}

export function SearchInput({ value, onChange, placeholder = 'Cerca...' }) {
    return (
        <div className="uiSearch">
            <i className="fas fa-search" aria-hidden="true" />
            <input
                type="search"
                className="form-control form-control-sm"
                value={value}
                placeholder={placeholder}
                aria-label={placeholder}
                onChange={e => onChange(e.target.value)}
            />
        </div>
    );
}

/**
 * Pulsante testuale con icona per le azioni sulle righe (Modifica, Elimina...).
 * variant: default | danger | warning | success
 */
export function TextButton({ icon, variant, onClick, children, title }) {
    const variantClass = variant ? ` is${variant.charAt(0).toUpperCase()}${variant.slice(1)}` : '';

    return (
        <button type="button" className={`uiTextBtn${variantClass}`} onClick={onClick} title={title}>
            {icon && <i className={icon} aria-hidden="true" />}
            {children}
        </button>
    );
}

export function Badge({ variant, children }) {
    return <span className={`uiBadge${variant ? ` is${variant.charAt(0).toUpperCase()}${variant.slice(1)}` : ''}`}>{children}</span>;
}

/**
 * Bandiera e nome del paese a partire dalla sigla (XX = nessun paese).
 */
export function Country({ code, showCode }) {
    if (!code || code === 'XX') return <span className="uiMuted">—</span>;

    return (
        <span className="uiCountry">
            <img src={`${process.env.PUBLIC_URL}/flags/${code}.svg`} alt="" loading="lazy" />
            <span>{returnNationality(code) || code}{showCode && <span className="uiMuted"> ({code})</span>}</span>
        </span>
    );
}

/**
 * Lista a colonne: su desktop è una tabella, su smartphone ogni riga diventa una scheda con etichetta e valore.
 *
 * @param {Array} columns [{ key, label, width, align: 'right'|'center', numeric, render: (row) => node }]
 * @param {Array} rows righe da mostrare
 * @param {Function} rowKey (row) => chiave univoca
 * @param {Function} actions (row) => pulsanti della riga (opzionale)
 * @param {String} empty messaggio se non ci sono righe
 * @param {Boolean} inline se true su smartphone la riga resta orizzontale (per liste a una sola colonna)
 * @param {String} actionsWidth larghezza fissa della colonna azioni (uguale in tutte le righe, così le colonne restano allineate)
 */
export function DataList({ columns, rows, rowKey, actions, empty = 'Nessun elemento', actionsWidth = '190px', inline = false }) {
    const template = [...columns.map(col => col.width || 'minmax(0, 1fr)'), ...(actions ? [actionsWidth] : [])].join(' ');
    const cellClass = (col, i) => [
        'uiCell',
        i === 0 ? 'isPrimary' : '',
        col.align === 'right' ? 'isRight' : col.align === 'center' ? 'isCenter' : '',
        col.numeric ? 'isNumeric' : ''
    ].filter(Boolean).join(' ');

    if (!rows.length) return <div className="uiEmpty">{empty}</div>;

    return (
        <div className={`uiList${inline ? ' isInline' : ''}`} style={{ '--ui-cols': template }} role="table">
            <div className="uiListHead" role="row">
                {columns.map((col, i) => <div key={col.key} className={cellClass(col, i)} role="columnheader">{col.label}</div>)}
                {actions && <div className="uiCell" role="columnheader"><span className="visually-hidden">Azioni</span></div>}
            </div>

            {
                rows.map(row => (
                    <div className="uiListRow" key={rowKey(row)} role="row">
                        {
                            columns.map((col, i) => (
                                <div key={col.key} className={cellClass(col, i)} data-label={col.label} role="cell">
                                    {col.render ? col.render(row) : row[col.key]}
                                </div>
                            ))
                        }
                        {actions && <div className="uiCell uiCellActions" role="cell">{actions(row)}</div>}
                    </div>
                ))
            }
        </div>
    );
}

/**
 * Pulsanti Flush e Flush CONFIG, comuni a tutte le pagine delle preferenze.
 */
export function FlushButtons({ canFlush, canFlushConfig, onFlush, onFlushConfig }) {
    return (
        <>
            {
                canFlush &&
                <button type="button" className="btn btn-sm btn-outline-warning" onClick={onFlush}>
                    <i className="fas fa-broom" aria-hidden="true" /> Flush
                </button>
            }
            {
                canFlushConfig &&
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={onFlushConfig}>
                    <i className="fas fa-broom" aria-hidden="true" /> Flush CONFIG
                </button>
            }
        </>
    );
}

/**
 * Filtro testuale case-insensitive su più campi.
 */
export function matchSearch(search, ...values) {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return values.some(value => String(value || '').toLowerCase().includes(term));
}
